import { motion } from 'framer-motion';

// [F14-P1-E] Directional page transitions
// Forward (going deeper): new page enters from right, old exits to left
// Back (going shallower): new page enters from left, old exits to right
// Fade: same-depth navigation — opacity only
// Using 30% offset (not 100%) — full-screen slides are jarring for a data-heavy app;
// 30% conveys direction clearly without sacrificing readability during animation.
export type TransitionDirection = 'forward' | 'back' | 'fade';

// 300ms ease-out (cubic-bezier(0, 0, 0.2, 1)) per research 2026-05-01.
// Was 250ms ease-in-out. Ease-out feels more decisive on route entry —
// content decelerates into rest rather than smearing through the middle.
// Fade direction also picks up an 8px translateY rise for subtle weight.
const TRANSITION = { duration: 0.30, ease: [0, 0, 0.2, 1] as const };

const variants = {
  initial: (dir: TransitionDirection) => ({
    x: dir === 'forward' ? '30%' : dir === 'back' ? '-30%' : 0,
    y: dir === 'fade' ? -8 : 0,
    opacity: 0,
  }),
  animate: {
    x: 0,
    y: 0,
    opacity: 1,
    transition: TRANSITION,
  },
  exit: (dir: TransitionDirection) => ({
    x: dir === 'forward' ? '-30%' : dir === 'back' ? '30%' : 0,
    y: dir === 'fade' ? 8 : 0,
    opacity: 0,
    transition: TRANSITION,
  }),
};

interface PageTransitionProps {
  children: React.ReactNode;
  custom?: TransitionDirection;
}

export default function PageTransition({ children, custom = 'fade' }: PageTransitionProps) {
  return (
    <motion.div
      custom={custom}
      variants={variants}
      initial="initial"
      animate="animate"
      exit="exit"
      style={{ willChange: 'transform, opacity' }}
    >
      {children}
    </motion.div>
  );
}
